import { toNullableNumber, toNumber } from 'model/mappers/scorecardValues';

import type {
  ShopPerformanceScore,
  ShopPerformanceScorePayload,
  SpsBenefit,
  SpsBenefitPayload,
  SpsDimension,
  SpsDimensionPayload,
  SpsMetric,
  SpsMetricPayload,
  SpsTopIssue,
  SpsTopIssuePayload,
  TiktokShopConnectionsPayload
} from 'model/types/shop-performance';

function toMetric(payload: SpsMetricPayload): SpsMetric {
  return {
    metricCode: payload.metricCode,
    metricName: payload.metricName,
    dimension: payload.dimension,
    score: toNullableNumber(payload.score) ?? undefined,
    value: toNumber(payload.value),
    valueUnit: payload.valueUnit,
    status: payload.status,
    statusText: payload.statusText,
    excellentThreshold: toNumber(payload.excellentThreshold),
    poorThreshold: toNumber(payload.poorThreshold),
    topReasonText: payload.topReasonText ?? undefined,
    evaluateDurationDays: payload.evaluateDurationDays ?? undefined,
    startEvaluationTime: payload.startEvaluationTime ?? undefined,
    endEvaluationTime: payload.endEvaluationTime ?? undefined
  };
}

function toBenefit(payload: SpsBenefitPayload): SpsBenefit {
  return {
    benefitName: payload.benefitName,
    unlockScore: toNumber(payload.unlockScore),
    isUnlocked: payload.isUnlocked
  };
}

function toDimension(payload: SpsDimensionPayload): SpsDimension {
  return {
    name: payload.name,
    score: toNumber(payload.score),
    status: payload.status,
    statusText: payload.statusText,
    weight: toNumber(payload.weight)
  };
}

function toTopIssue(payload: SpsTopIssuePayload): SpsTopIssue {
  return {
    rank: payload.rank,
    metricCode: payload.metricCode,
    description: payload.description,
    evaluateDurationDays: payload.evaluateDurationDays ?? undefined,
    startEvaluationTime: payload.startEvaluationTime ?? undefined,
    endEvaluationTime: payload.endEvaluationTime ?? undefined
  };
}

function toShopPerformanceScore(payload: ShopPerformanceScorePayload): ShopPerformanceScore | null {
  // A missing score is not a zero, it is "not available yet".
  const score = toNullableNumber(payload.score);
  if (score === null) return null;

  return {
    score,
    tier: payload.tier,
    tierText: payload.tierText,
    updatedAt: payload.updatedAt,
    topIssuesSummary: payload.topIssuesSummary,
    metrics: (payload.metrics ?? []).map(toMetric),
    benefits: (payload.benefits ?? []).map(toBenefit),
    topIssues: (payload.topIssues ?? []).map(toTopIssue),
    dimensions: (payload.dimensions ?? []).map(toDimension)
  };
}

export function mapShopPerformanceScore(connections?: TiktokShopConnectionsPayload | null): ShopPerformanceScore | null {
  const payload = connections?.edges?.[0]?.node?.sps;
  return payload ? toShopPerformanceScore(payload) : null;
}

export function hasTiktokShopConnection(connections?: TiktokShopConnectionsPayload | null): boolean {
  return (connections?.edges?.length ?? 0) > 0;
}
